import { t, Trans } from "@lingui/macro";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";

type Agent = {
  id: string;
  name: string;
  codename: string;
  role: string;
  tools: string[];
  image: string;
  glow: string;
  ring: string;
};

const AgentCard = ({ agent, index, reduceMotion }: {
  agent: Agent;
  index: number;
  reduceMotion: boolean;
}) => (
  <motion.article
    initial={reduceMotion ? false : { opacity: 0, y: 20 }}
    whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.4, delay: (index % 5) * 0.05 }}
    className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-white/[0.02] p-4 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.04]"
  >
    {/* Character image with glow */}
    <div className="relative mb-3 aspect-square overflow-hidden rounded-xl bg-gradient-to-br from-white/5 to-transparent">
      <div
        className={`absolute inset-0 bg-gradient-to-br ${agent.glow} opacity-0 transition-opacity duration-500 group-hover:opacity-100`}
      />
      <img
        src={agent.image}
        alt={agent.name}
        loading="lazy"
        decoding="async"
        className="relative size-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
      />
      {/* Step number tag */}
      <div className="absolute left-2 top-2 flex size-6 items-center justify-center rounded-full bg-black/60 font-mono text-[10px] font-bold text-white backdrop-blur-md ring-1 ring-white/20">
        {String(index + 1).padStart(2, "0")}
      </div>
    </div>

    {/* Codename + name */}
    <div className="mb-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
        {agent.codename}
      </div>
      <h3 className="text-sm font-bold text-white">{agent.name}</h3>
    </div>

    {/* Role */}
    <p className="flex-1 text-[11px] leading-relaxed text-white/55">{agent.role}</p>
  </motion.article>
);

export const AgentSquadSection = () => {
  const reduceMotion = useReducedMotion() ?? false;

  // Defined inside the component so `t` macro resolves with the current locale on every render.
  // Brand/product names (O*NET, Exa.ai, Claude, GPT-4o, USA, EU, GCC, etc.) are intentionally
  // kept literal — they shouldn't translate.
  const ORCHESTRATOR: Agent = {
    id: "orchestrator",
    name: t`Orchestrator`,
    codename: t`THE CONDUCTOR`,
    role: t`Runs your entire resume squad in the right order so you get a polished draft without managing the process yourself`,
    tools: [t`State machine`, t`Multi-agent`],
    image: "/11-agents/new-11-agents/orchestrator.png",
    glow: "from-yellow-400/40 to-amber-600/0",
    ring: "ring-yellow-400/50",
  };

  const AGENTS: Agent[] = [
    {
      id: "market-scout",
      name: t`Market Scout`,
      codename: t`THE EXPLORER`,
      role: t`Surfaces what employers actually want for your role so your resume speaks their language from line one`,
      tools: ["O*NET", "Exa.ai", "Perplexity"],
      image: "/11-agents/new-11-agents/agent_market_scout.png",
      glow: "from-amber-500/30 to-amber-700/0",
      ring: "ring-amber-400/40",
    },
    {
      id: "interrogator",
      name: t`Interrogator`,
      codename: t`THE AUDITOR`,
      role: t`Pulls measurable wins and proof from your experience so your bullets read credible, not vague`,
      tools: ["Claude 3.5", t`Voice & Text`],
      image: "/11-agents/new-11-agents/interrogator.png",
      glow: "from-rose-500/30 to-rose-700/0",
      ring: "ring-rose-400/40",
    },
    {
      id: "truth-check",
      name: t`Truth Check`,
      codename: t`THE SKEPTIC`,
      role: t`Keeps every claim tied to real evidence so you never submit inflated or fabricated copy`,
      tools: [t`Verification`, t`Cross-check`],
      image: "/11-agents/new-11-agents/agent_truth_check.png",
      glow: "from-emerald-500/30 to-emerald-700/0",
      ring: "ring-emerald-400/40",
    },
    {
      id: "verified-skills",
      name: t`Verified Skills`,
      codename: t`THE VALIDATOR`,
      role: t`Backs each skill with proof from your work history so recruiters trust what they scan`,
      tools: [t`Skill graph`, t`Evidence map`],
      image: "/11-agents/new-11-agents/agent_verified_skills.png",
      glow: "from-blue-500/30 to-blue-700/0",
      ring: "ring-blue-400/40",
    },
    {
      id: "architect",
      name: t`Architect`,
      codename: t`THE STRATEGIST`,
      role: t`Structures sections and story flow so recruiters see your strongest case in seconds`,
      tools: [t`Multi-provider`, t`Structured`],
      image: "/11-agents/new-11-agents/agent_architect.png",
      glow: "from-indigo-500/30 to-indigo-700/0",
      ring: "ring-indigo-400/40",
    },
    {
      id: "market-aligned",
      name: t`Market Aligned`,
      codename: t`THE CALIBRATOR`,
      role: t`Tunes every line to what hiring teams buy for your target role, not generic filler`,
      tools: [t`Alignment`, t`Calibration`],
      image: "/11-agents/new-11-agents/agent_market_aligned.png",
      glow: "from-purple-500/30 to-purple-700/0",
      ring: "ring-purple-400/40",
    },
    {
      id: "writer",
      name: t`Writer`,
      codename: t`THE WORDSMITH`,
      role: t`Writes sharp, human-sounding bullets so you come across as hire-ready—not like a chatbot résumé`,
      tools: ["Claude", "GPT-4o", "DeepSeek"],
      image: "/11-agents/new-11-agents/agent_writer.png",
      glow: "from-fuchsia-500/30 to-fuchsia-700/0",
      ring: "ring-fuchsia-400/40",
    },
    {
      id: "ats",
      name: t`ATS Inspector`,
      codename: t`THE QA`,
      role: t`Scores your draft against the job posting and fixes gaps so you're more likely to clear ATS filters`,
      tools: ["GPT-4o", t`0–100 scoring`],
      image: "/11-agents/new-11-agents/agent_ats.png",
      glow: "from-cyan-500/30 to-cyan-700/0",
      ring: "ring-cyan-400/40",
    },
    {
      id: "formatter",
      name: t`Formatter`,
      codename: t`THE STYLIST`,
      role: t`Formats your resume for local hiring norms so it looks professional in your market, every time`,
      tools: ["USA", "EU", "GCC"],
      image: "/11-agents/new-11-agents/agent_formatter.png",
      glow: "from-teal-500/30 to-teal-700/0",
      ring: "ring-teal-400/40",
    },
    {
      id: "translator",
      name: t`Translator`,
      codename: t`THE POLYGLOT`,
      role: t`Delivers your full resume in 15+ languages—including RTL—so you can apply globally without rewriting from scratch`,
      tools: [t`15+ languages`, t`RTL ready`],
      image: "/11-agents/new-11-agents/agent_translator.png",
      glow: "from-sky-500/30 to-sky-700/0",
      ring: "ring-sky-400/40",
    },
  ];

  return (
    <section className="relative overflow-x-clip bg-background py-24 sm:py-32">
      {/* Atmospheric background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-[600px] w-full max-w-4xl -translate-x-1/2 rounded-full bg-yellow-500/5 blur-[180px]" />
        <div className="absolute left-1/4 bottom-0 size-96 rounded-full bg-fuchsia-500/5 blur-[140px]" />
        <div className="absolute right-1/4 top-1/3 size-96 rounded-full bg-brand-teal/5 blur-[140px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        {/* Header */}
        <div className="mx-auto mb-20 max-w-4xl text-center">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4 flex items-center justify-center gap-2"
          >
            <div className="h-px w-8 bg-gradient-to-r from-transparent to-yellow-400" />
            <span className="text-xs font-semibold uppercase tracking-wider text-yellow-400">
              <Trans>The Agentic Squad</Trans>
            </span>
            <div className="h-px w-8 bg-gradient-to-l from-transparent to-yellow-400" />
          </motion.div>

          <motion.h2
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mb-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl"
          >
            <Trans>11 AI specialists.</Trans>
            <br />
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
              <Trans>One mission: your resume.</Trans>
            </span>
          </motion.h2>

          <motion.p
            initial={reduceMotion ? false : { opacity: 0, y: 20 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mx-auto max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg"
          >
            <Trans>
              Behind every Stylion resume is a coordinated squad of specialized AI agents —
              each with one job, each great at it. Together they research, interrogate, write,
              verify, score, format, and translate. You just review and download.
            </Trans>
          </motion.p>
        </div>

        {/* Hero: The Conductor */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, scale: 0.95 }}
          whileInView={reduceMotion ? undefined : { opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-yellow-500/5 via-amber-500/5 to-transparent p-8 backdrop-blur-sm sm:p-12 lg:grid-cols-2 lg:gap-16">
            {/* Conductor image */}
            <div className="relative mx-auto aspect-square w-full max-w-sm">
              <div className="absolute inset-0 animate-pulse rounded-full bg-yellow-400/20 blur-3xl" />
              {!reduceMotion && (
                <>
                  {/* Orbiting rings */}
                  <div
                    className="absolute inset-0 rounded-full border border-yellow-400/20"
                    style={{ animation: "spin 20s linear infinite" }}
                  >
                    <div className="absolute -right-1 top-1/2 size-2 -translate-y-1/2 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50" />
                  </div>
                  <div
                    className="absolute inset-4 rounded-full border border-amber-400/15"
                    style={{ animation: "spin 30s linear infinite reverse" }}
                  >
                    <div className="absolute -left-1 top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-amber-400 shadow-lg shadow-amber-400/50" />
                  </div>
                </>
              )}
              <img
                src={ORCHESTRATOR.image}
                alt={ORCHESTRATOR.name}
                loading="lazy"
                decoding="async"
                className="relative size-full object-contain drop-shadow-[0_20px_50px_rgba(250,204,21,0.3)]"
              />
            </div>

            {/* Conductor text */}
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-yellow-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-yellow-300 ring-1 ring-yellow-400/20">
                <Sparkle weight="fill" className="size-3" />
                <Trans>Squad Captain</Trans>
              </div>
              <h3 className="mb-2 text-3xl font-bold text-foreground sm:text-4xl">
                {ORCHESTRATOR.name}
              </h3>
              <div className="mb-4 text-xs font-bold uppercase tracking-widest text-yellow-400/80">
                {ORCHESTRATOR.codename}
              </div>
              <p className="text-base leading-relaxed text-muted-foreground">
                <Trans>
                  Directs the full squad from research to final PDF so you get a job-ready resume
                  without juggling a dozen tools—or wondering which step comes next.
                </Trans>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Workflow phases label */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto mb-8 flex max-w-3xl flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70"
        >
          <span className="text-amber-400/70">
            <Trans>1. Discover</Trans>
          </span>
          <ArrowRight className="size-3" />
          <span className="text-emerald-400/70">
            <Trans>2. Verify</Trans>
          </span>
          <ArrowRight className="size-3" />
          <span className="text-indigo-400/70">
            <Trans>3. Strategize</Trans>
          </span>
          <ArrowRight className="size-3" />
          <span className="text-fuchsia-400/70">
            <Trans>4. Create</Trans>
          </span>
          <ArrowRight className="size-3" />
          <span className="text-cyan-400/70">
            <Trans>5. Polish</Trans>
          </span>
        </motion.div>

        {/* The 10 specialists grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {AGENTS.map((agent, i) => (
            <AgentCard key={agent.id} agent={agent} index={i} reduceMotion={reduceMotion} />
          ))}
        </div>

        {/* Footer CTA */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Button
            asChild
            size="lg"
            className="bg-yellow-400 text-black hover:bg-yellow-400/90"
          >
            <Link to="/ai-generator">
              <Trans>Put the Squad to Work</Trans>
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-white"
          >
            <Link to="/features/agentic-intelligence">
              <Trans>How They Work</Trans>
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
